/* oxlint-disable react/jsx-props-no-spreading */

import {
  createContext,
  InputHTMLAttributes,
  PropsWithChildren,
  useContext,
  useMemo,
  useState,
} from 'react';
import { twMerge } from 'tailwind-merge';
import Field from './Field';

type InputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'children' | 'hidden'
>;

const GroupContext = createContext<{
  selectedValue: string | null;
  isInvalid: boolean;
}>({ selectedValue: null, isInvalid: false });

function Radio({ value, checked, defaultChecked, ...inputProps }: InputProps) {
  const { isInvalid } = useContext(GroupContext);

  return (
    <input
      {...inputProps}
      value={value}
      checked={checked}
      defaultChecked={defaultChecked}
      type="radio"
      className={twMerge(
        'border-outline bg-transparent checked:border-brand checked:bg-brand checked:ring-offset-0',
        'disabled:border-on-surface/(--state-disabled-mix) disabled:checked:border-on-surface/(--state-disabled-mix) disabled:checked:bg-on-surface/(--state-disabled-mix)',
        'hover:border-on-surface hover:checked:border-brand',
        'focus:ring-0 focus-visible:outline-(length:--state-focused-outline-width) focus-visible:outline-offset-(--state-focused-outline-offset) focus-visible:outline-(color:--state-focused-outline-color)',
        isInvalid &&
          'border-critical text-critical hover:border-on-critical-container focus-visible:outline-on-critical-container',
      )}
    />
  );
}

export function ButtonOption({
  children,
  className,
  ...inputProps
}: PropsWithChildren<InputProps>) {
  return (
    <Field.Label
      className={twMerge(
        'flex items-center gap-3 p-2 focus:ring-0 focus:outline-hidden',
        'has-disabled:pointer-events-none has-disabled:text-on-surface/(--state-disabled-mix)',
        className,
      )}
      // ? right place for this?
      data-component="corso-radio-button-option"
    >
      <Radio {...inputProps} />
      {children}
    </Field.Label>
  );
}

// I would like to extract this into it's own component like the [SelectBoxGroup](https://react-spectrum.adobe.com/SelectBoxGroup)
export function CardOption({
  children,
  className,
  ...inputProps
}: PropsWithChildren<InputProps>) {
  const { isInvalid } = useContext(GroupContext);

  return (
    <Field.Label
      className={twMerge(
        // base
        'group flex w-full items-start gap-4 rounded-lg border border-outline-variant bg-surface-container-lowest p-6 shadow-xs',
        // checked
        'has-checked:border-brand has-checked:shadow-none has-checked:ring-2 has-checked:ring-brand',
        // disabled
        'has-disabled:pointer-events-none has-disabled:border-outline/(--state-disabled-mix) has-disabled:text-on-surface/(--state-disabled-mix) has-disabled:shadow-none',
        'has-disabled:has-checked:border-brand/(--state-disabled-mix) has-disabled:has-checked:ring-brand/(--state-disabled-mix)',
        // hovered
        'hover:bg-mix hover:mix-with-on-surface/(--state-hovered-mix) hover:mix-surface-container-lowest',
        // focused
        'focus-within:outline-(length:--state-focused-outline-width) focus-within:outline-offset-(--state-focused-outline-offset) focus-within:outline-(color:--state-focused-outline-color)',
        'focus-within:border-on-surface focus-within:bg-mix focus-within:shadow-none focus-within:mix-with-on-surface/(--state-focused-mix) focus-within:mix-surface-container-lowest',
        // invalid
        isInvalid &&
          'border-critical focus-within:border-on-critical-container focus-within:outline-on-critical-container',
        className,
      )}
      data-component="corso-radio-card-option"
    >
      <div className="min-w-0 flex-1">{children}</div>
      <Radio {...inputProps} />
    </Field.Label>
  );
}

type ValidationConstraint = Extract<keyof ValidityState, 'valueMissing'>;

export default function RadioGroup({
  className = '',
  label,
  showLabel = true,
  children,
  disabled,
  validationMessage,
}: PropsWithChildren<{
  className?: string;
  label: string;
  showLabel?: boolean;
  disabled?: boolean;
  validationMessage?: Record<ValidationConstraint, string>;
}>) {
  const [isInvalid, setIsInvalid] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedValue, setSelectedValue] = useState<string | null>(null);

  const providerState = useMemo(
    () => ({ selectedValue, isInvalid }),
    [selectedValue, isInvalid],
  );

  const handleInvalid = (event: React.FormEvent<HTMLFieldSetElement>) => {
    if (event.target instanceof HTMLInputElement) {
      setIsInvalid(!event.target.validity.valid);
      setErrorMessage(event.target.validationMessage);
    }
  };

  const handleChange = (event: React.FormEvent<HTMLFieldSetElement>) => {
    if (event.target instanceof HTMLInputElement) {
      setSelectedValue(event.target.value);
      setIsInvalid(!event.target.validity.valid);
    }
  };

  return (
    <fieldset
      disabled={disabled}
      className={className}
      onInvalid={handleInvalid}
      onChange={handleChange}
      data-component="corso-radio-group"
    >
      <legend
        className={twMerge(
          'label-lg text-on-surface-variant',
          !showLabel && 'sr-only',
        )}
      >
        {label}
      </legend>
      <GroupContext.Provider value={providerState}>
        <div
        // this div is for the borders radius on the button options
        >
          {children}
        </div>
      </GroupContext.Provider>
      {!!isInvalid && (
        <Field.ErrorText className="mt-2">
          {validationMessage?.valueMissing ?? errorMessage}
        </Field.ErrorText>
      )}
    </fieldset>
  );
}
